import React, { useMemo } from 'react';
import { AlertCircle, CheckCircle, Database, Copy, Check } from 'lucide-react';
import './UsedVariablesPanel.css';

const UsedVariablesPanel = ({ blocks = [] }) => {
  const [copiedKey, setCopiedKey] = React.useState(null);

  // Extract all variables from template blocks
  const usedVariables = useMemo(() => {
    const variables = new Map(); // key -> { count, locations }

    // Regex to find {{variable}} patterns (including pipes for defaults)
    const variableRegex = /\{\{([^}]+)\}\}/g;

    // Helper to extract variable name (before any pipe for default value)
    const extractVarName = (match) => {
      // Handle {{variable | "default"}} syntax
      const parts = match.split('|');
      return parts[0].trim();
    };

    // Scan each block's content
    blocks.forEach((block, index) => {
      const blockName = `${block.type} block ${index + 1}`;

      // Check different block config fields that might contain variables
      const fieldsToCheck = [
        block.config?.body,
        block.config?.headline,
        block.config?.subheadline,
        block.config?.title,
        block.config?.description,
        block.config?.primaryText,
        block.config?.secondaryText,
        block.config?.html,
        block.config?.companyName,
        block.config?.address,
      ];

      fieldsToCheck.forEach(field => {
        if (typeof field === 'string') {
          let match;
          while ((match = variableRegex.exec(field)) !== null) {
            const fullMatch = match[1]; // Content inside {{ }}
            const varName = extractVarName(fullMatch);

            if (variables.has(varName)) {
              const existing = variables.get(varName);
              existing.count++;
              if (!existing.locations.includes(blockName)) {
                existing.locations.push(blockName);
              }
            } else {
              variables.set(varName, {
                name: varName,
                fullSyntax: `{{${fullMatch}}}`,
                count: 1,
                locations: [blockName],
                hasDefault: fullMatch.includes('|'),
              });
            }
          }
        }
      });
    });

    return Array.from(variables.values());
  }, [blocks]);

  const copyToClipboard = async (varName) => {
    try {
      await navigator.clipboard.writeText(`{{${varName}}}`);
      setCopiedKey(varName);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (usedVariables.length === 0) {
    return (
      <div className="used-variables-panel">
        <div className="used-variables-empty">
          <Database size={24} />
          <p>No variables detected in this template</p>
          <span>Add tags like <code>{'{{userName}}'}</code> to your content</span>
        </div>
      </div>
    );
  }

  return (
    <div className="used-variables-panel">
      <div className="used-variables-header">
        <div className="used-variables-count">
          <CheckCircle size={16} />
          <span><strong>{usedVariables.length}</strong> variable{usedVariables.length !== 1 ? 's' : ''} detected</span>
        </div>
        <div className="used-variables-hint">
          <AlertCircle size={12} />
          <span>These need values when sending</span>
        </div>
      </div>

      <div className="used-variables-list">
        {usedVariables.map((variable) => (
          <div key={variable.name} className="used-variable-item">
            <div className="used-variable-main">
              <code className="used-variable-name">{`{{${variable.name}}}`}</code>
              <button
                className="used-variable-copy"
                onClick={() => copyToClipboard(variable.name)}
                title="Copy variable"
              >
                {copiedKey === variable.name ? (
                  <Check size={12} className="copied" />
                ) : (
                  <Copy size={12} />
                )}
              </button>
            </div>
            <div className="used-variable-meta">
              <span className="used-variable-count">
                Used {variable.count}x
              </span>
              {variable.hasDefault && (
                <span className="used-variable-badge default">has default</span>
              )}
            </div>
            <div className="used-variable-locations">
              {variable.locations.map((loc, i) => (
                <span key={i} className="used-variable-location">{loc}</span>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="used-variables-footer">
        <strong>Required Data Structure:</strong>
        <pre className="used-variables-schema">
{`{
${usedVariables.map(v => `  "${v.name}": "value"`).join(',\n')}
}`}
        </pre>
      </div>
    </div>
  );
};

export default UsedVariablesPanel;
